import { useState, useEffect } from 'react';
import { useAuthFetch } from '@/hooks/useAdmin';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Tags() {
  const authFetch = useAuthFetch();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadTags(); }, []);

  async function loadTags() {
    try {
      const res = await authFetch(`${API}/admin/tags`);
      if (res.ok) { const d = await res.json(); setTags(d.tags || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function createTag() {
    if (!newTag.trim()) return;
    setCreating(true);
    try {
      const res = await authFetch(`${API}/admin/tags`, { method: 'POST', body: JSON.stringify({ name: newTag.trim() }) });
      if (res.ok) { toast.success('Oznaka stvorena'); setNewTag(''); loadTags(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Greska'); }
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  }

  async function deleteTag(id) {
    if (!window.confirm('Obrisati oznaku?')) return;
    await authFetch(`${API}/admin/tags/${id}`, { method: 'DELETE' });
    loadTags();
  }

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="tags-page">
      <h1 className="text-2xl font-semibold mb-6">Oznake</h1>

      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={newTag}
          onChange={e => setNewTag(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && createTag()}
          placeholder="Nova oznaka..."
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-600 placeholder-zinc-600"
          data-testid="new-tag-input"
        />
        <button onClick={createTag} disabled={creating || !newTag.trim()} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md disabled:opacity-40" data-testid="create-tag-btn">
          <Plus size={16} /> Dodaj
        </button>
      </div>

      {tags.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">Nema oznaka</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="tags-table">
            <thead>
              <tr className="border-b border-zinc-800 text-zinc-500 text-left">
                <th className="px-4 py-3 font-medium">Naziv</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium w-20">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {tags.map(t => (
                <tr key={t.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="px-4 py-3 text-zinc-200">{t.name}</td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => deleteTag(t.id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`delete-tag-${t.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
